// ─────────────────────────────────────────────────────────────────
// Glass-i — Grid query helpers
//
// Pure functions for translating between the grid's internal
// `GridQueryState` (see gridTypes.js) and the on-wire formats used
// by the backend list endpoints.
//
// Wire format (default GET querystring, backward-compatible with
// existing /api/* list APIs that only know page+pageSize):
//
//   page=1
//   pageSize=25
//   search=foo
//   sort=customer_name:asc,created_at:desc
//   filter[status]=active
//   filter[stateCode]=27,29     // multi-value
//   filter[created_at]=2026-01-01..2026-03-31  // dateRange
//
// Backend response — both legacy and standard shapes are accepted by
// `normaliseListResponse` so a master can opt in to the new envelope
// without forcing a coordinated rollout:
//
//   { rows, total, page?, pageSize?, summary?, meta? }   // standard
//   { items, total }                                     // legacy A
//   { data, total }                                      // legacy B
//   { rows, count }                                      // legacy C
//   [ ...rows ]                                          // legacy D
//
// Anything else falls through to `{ rows: [], total: 0 }`.
// ─────────────────────────────────────────────────────────────────

/** @typedef {import('./gridTypes').GridQueryState}  GridQueryState  */
/** @typedef {import('./gridTypes').GridSortSpec}    GridSortSpec    */
/** @typedef {import('./gridTypes').GridFilterSpec}  GridFilterSpec  */
/** @typedef {import('./gridTypes').GridDataResponse} GridDataResponse */

export const DEFAULT_PAGE = 1;
export const DEFAULT_PAGE_SIZE = 25;
export const DEFAULT_PAGE_SIZE_OPTIONS = ['15', '25', '50', '100'];

/**
 * Build a fresh `GridQueryState` with defaults applied. Useful as the
 * seed for reducer-style grid state.
 *
 * @param {Partial<GridQueryState>} [overrides]
 * @returns {GridQueryState}
 */
export function makeQueryState(overrides = {}) {
  return {
    page: overrides.page ?? DEFAULT_PAGE,
    pageSize: overrides.pageSize ?? DEFAULT_PAGE_SIZE,
    search: overrides.search ?? '',
    sort: overrides.sort ?? [],
    filters: overrides.filters ?? [],
    advancedFilters: overrides.advancedFilters,
    visibleColumns: overrides.visibleColumns,
    groupBy: overrides.groupBy
  };
}

/**
 * Serialise a `GridSortSpec[]` to the compact `field:dir,field:dir`
 * form used in the querystring. Returns an empty string when no sort
 * is active so callers can skip the key entirely.
 *
 * @param {GridSortSpec[]} [sort]
 * @returns {string}
 */
export function serialiseSort(sort) {
  if (!Array.isArray(sort) || sort.length === 0) return '';
  return sort
    .map((s) => `${s.field}:${s.direction === 'desc' ? 'desc' : 'asc'}`)
    .join(',');
}

/**
 * Parse the compact sort form back into a `GridSortSpec[]`.
 *
 * @param {string} [raw]
 * @returns {GridSortSpec[]}
 */
export function parseSort(raw) {
  if (!raw) return [];
  return String(raw)
    .split(',')
    .map((tok) => tok.trim())
    .filter(Boolean)
    .map((tok) => {
      const [field, dir] = tok.split(':');
      return {
        field,
        direction: dir === 'desc' ? 'desc' : 'asc'
      };
    });
}

/**
 * Encode a single filter value. Arrays use comma-join; primitives
 * stringify. Objects (used for dateRange / between) are encoded as
 * `from..to` for compactness.
 */
function encodeFilterValue(value) {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(String).join(',');
  if (typeof value === 'object') {
    if ('from' in value || 'to' in value) {
      return `${value.from ?? ''}..${value.to ?? ''}`;
    }
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Serialise an entire `GridQueryState` into a plain object suitable
 * for `URLSearchParams` or `axios.get({ params })`. Empty values are
 * omitted so the wire stays compact.
 *
 * @param {GridQueryState} state
 * @returns {Record<string,string>}
 */
export function serialiseQueryState(state) {
  const out = {};
  if (state.page) out.page = String(state.page);
  if (state.pageSize) out.pageSize = String(state.pageSize);
  if (state.search) out.search = String(state.search);
  const sortStr = serialiseSort(state.sort);
  if (sortStr) out.sort = sortStr;
  if (Array.isArray(state.filters)) {
    for (const f of state.filters) {
      if (!f || !f.field) continue;
      const encoded = encodeFilterValue(f.value);
      if (encoded !== '') out[`filter[${f.field}]`] = encoded;
    }
  }
  if (state.groupBy) out.groupBy = state.groupBy;
  // advancedFilters is sent as a JSON-encoded string ONLY when it has
  // rules — keeps the GET URL clean for the common case.
  if (state.advancedFilters && state.advancedFilters.rules?.length) {
    out.advancedFilters = JSON.stringify(state.advancedFilters);
  }
  return out;
}

/**
 * Build a querystring (without leading `?`) from a `GridQueryState`.
 *
 * @param {GridQueryState} state
 * @returns {string}
 */
export function toQueryString(state) {
  const params = new URLSearchParams(serialiseQueryState(state));
  return params.toString();
}

/**
 * Normalise any of the supported list-response shapes into the
 * canonical `GridDataResponse`. Resilient to envelope drift so a
 * single grid can talk to old and new endpoints transparently.
 *
 * @param {any} raw
 * @param {GridQueryState} [requested] — falls back to echo page/pageSize when the API omits them
 * @returns {GridDataResponse}
 */
export function normaliseListResponse(raw, requested) {
  if (!raw) return { rows: [], total: 0 };
  if (Array.isArray(raw)) {
    return {
      rows: raw,
      total: raw.length,
      page: requested?.page,
      pageSize: requested?.pageSize
    };
  }
  const rows =
    raw.rows ??
    raw.items ??
    raw.data ??
    raw.records ??
    raw.results ??
    [];
  const total =
    raw.total ??
    raw.count ??
    raw.totalRows ??
    (Array.isArray(rows) ? rows.length : 0);
  return {
    rows: Array.isArray(rows) ? rows : [],
    total: Number.isFinite(total) ? total : 0,
    page: raw.page ?? requested?.page,
    pageSize: raw.pageSize ?? requested?.pageSize,
    summary: raw.summary,
    meta: raw.meta
  };
}

// ─────────────────────────────────────────────────────────────────
// Advanced-filter client-side evaluator
//
// Used in client mode so the demo + future small datasets honour
// the same `GridAdvancedFilterGroup` shape that server mode posts to
// the backend. Server mode skips this entirely — the API already
// applied the filter. Pure function; no React deps.
// ─────────────────────────────────────────────────────────────────

function readPath(record, path) {
  if (!record || path == null) return undefined;
  if (Array.isArray(path)) {
    let cur = record;
    for (const k of path) {
      if (cur == null) return undefined;
      cur = cur[k];
    }
    return cur;
  }
  if (typeof path === 'string' && path.includes('.')) {
    return path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), record);
  }
  return record[path];
}

function isEmptyish(v) {
  return v === null || v === undefined || v === '';
}

function compareNumeric(a, b) {
  if (a == null || b == null) return NaN;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  // Date comparison via ISO/parseable strings.
  const da = new Date(a).getTime();
  const db = new Date(b).getTime();
  if (Number.isFinite(da) && Number.isFinite(db)) return da - db;
  return NaN;
}

/**
 * Evaluate a single `GridFilterSpec` against a record. Returns boolean.
 *
 * Note on loose equality: `equals` and `notEquals` deliberately use
 * `==` / `!=` so a string "5" filter matches a numeric 5 cell. ERP
 * data often comes in mixed types (e.g. enum codes as strings, ids
 * as numbers) and exact `===` matches would surprise users. The
 * narrower numeric/date ops (`gt`/`gte`/`lt`/`lte`/`between`) use the
 * stricter `compareNumeric` helper.
 *
 * @param {any} record
 * @param {GridFilterSpec} rule
 */
export function evaluateRule(record, rule) {
  if (!rule || !rule.field) return true;
  const v = readPath(record, rule.field);
  const op = rule.op || 'equals';

  switch (op) {
    case 'isEmpty':
      return isEmptyish(v);
    case 'isNotEmpty':
      return !isEmptyish(v);
    case 'equals':
      // eslint-disable-next-line eqeqeq
      return v == rule.value;
    case 'notEquals':
      // eslint-disable-next-line eqeqeq
      return v != rule.value;
    case 'contains':
      return v != null && String(v).toLowerCase().includes(String(rule.value ?? '').toLowerCase());
    case 'startsWith':
      return v != null && String(v).toLowerCase().startsWith(String(rule.value ?? '').toLowerCase());
    case 'endsWith':
      return v != null && String(v).toLowerCase().endsWith(String(rule.value ?? '').toLowerCase());
    case 'in': {
      const list = Array.isArray(rule.value) ? rule.value : [rule.value];
      // eslint-disable-next-line eqeqeq
      return list.some((x) => x == v);
    }
    case 'gt': {
      const c = compareNumeric(v, rule.value);
      return Number.isFinite(c) && c > 0;
    }
    case 'gte': {
      const c = compareNumeric(v, rule.value);
      return Number.isFinite(c) && c >= 0;
    }
    case 'lt': {
      const c = compareNumeric(v, rule.value);
      return Number.isFinite(c) && c < 0;
    }
    case 'lte': {
      const c = compareNumeric(v, rule.value);
      return Number.isFinite(c) && c <= 0;
    }
    case 'between': {
      const { from, to } = rule.value || {};
      if (from === undefined && to === undefined) return true;
      if (from !== undefined) {
        const c = compareNumeric(v, from);
        if (!Number.isFinite(c) || c < 0) return false;
      }
      if (to !== undefined) {
        const c = compareNumeric(v, to);
        if (!Number.isFinite(c) || c > 0) return false;
      }
      return true;
    }
    default:
      return true;
  }
}

/**
 * Evaluate an entire advanced-filter group against a record. Recurses
 * through nested groups; supports `and` (default) and `or`.
 *
 * @param {any} record
 * @param {GridAdvancedFilterGroup|null|undefined} group
 */
export function evaluateAdvancedFilters(record, group) {
  if (!group || !Array.isArray(group.rules) || group.rules.length === 0) return true;
  const combinator = group.combinator === 'or' ? 'or' : 'and';
  const evalOne = (node) => {
    if (node && Array.isArray(node.rules)) return evaluateAdvancedFilters(record, node);
    return evaluateRule(record, node);
  };
  return combinator === 'and' ? group.rules.every(evalOne) : group.rules.some(evalOne);
}

// ─────────────────────────────────────────────────────────────────
// Advanced-filter helpers (Stage 6A Increment 3)
//
// Pure utilities shared by the nested AND/OR modal UI and any other
// caller that needs to inspect, normalise, or prune an
// advanced-filter group. The backend parser already enforces its own
// max-depth/rule-count limits — these helpers mirror those so the UI
// can prevent users from authoring a group the backend will reject.
// ─────────────────────────────────────────────────────────────────

/**
 * Max nesting depth for the nested AND/OR filter UI. Matches the
 * backend parser default (DEFAULT_MAX_DEPTH=4) so a UI-authored group
 * round-trips through the server without losing rules.
 */
export const MAX_ADVANCED_FILTER_DEPTH = 4;

/**
 * Detect whether a node is a group (has a `rules` array) vs a leaf
 * rule (`field`/`op`/`value`). A blank/unknown node is treated as a
 * leaf so the recursion stops cleanly.
 */
function isGroupNode(node) {
  return Boolean(node && Array.isArray(node.rules));
}

/**
 * Detect whether a leaf rule has been authored enough to be evaluable.
 *  - field is required
 *  - isEmpty / isNotEmpty need no value
 *  - between needs at least one bound
 *  - in needs a non-empty array
 *  - everything else needs a value that is not "", null or undefined
 */
function isCompleteRule(rule) {
  if (!rule || typeof rule !== 'object') return false;
  if (typeof rule.field !== 'string' || !rule.field) return false;
  const op = rule.op || 'equals';
  if (op === 'isEmpty' || op === 'isNotEmpty') return true;
  if (op === 'between') {
    return Boolean(
      rule.value &&
        typeof rule.value === 'object' &&
        (rule.value.from !== undefined || rule.value.to !== undefined)
    );
  }
  if (op === 'in') return Array.isArray(rule.value) && rule.value.length > 0;
  return rule.value !== undefined && rule.value !== null && rule.value !== '';
}

/**
 * Compute the depth of an advanced-filter group. A flat group counts
 * as depth 1; one level of nesting is depth 2. Useful for the modal
 * UI's "Add group" button which must disable once the limit is hit.
 *
 * @param {GridAdvancedFilterGroup|null|undefined} group
 * @returns {number}
 */
export function getAdvancedFilterDepth(group) {
  if (!group || !Array.isArray(group.rules) || group.rules.length === 0) return 0;
  let maxChild = 0;
  for (const node of group.rules) {
    if (isGroupNode(node)) {
      const d = getAdvancedFilterDepth(node);
      if (d > maxChild) maxChild = d;
    }
  }
  return 1 + maxChild;
}

/**
 * Count the leaf rules inside a group (recursive). Group nodes themselves
 * are not counted — only the terminal field/op/value rules. The toolbar
 * badge uses this to surface the active rule count to the user.
 *
 * @param {GridAdvancedFilterGroup|null|undefined} group
 * @returns {number}
 */
export function countAdvancedFilterRules(group) {
  if (!group || !Array.isArray(group.rules)) return 0;
  let n = 0;
  for (const node of group.rules) {
    if (isGroupNode(node)) n += countAdvancedFilterRules(node);
    else if (isCompleteRule(node)) n += 1;
  }
  return n;
}

/**
 * Recursively drop empty / incomplete nodes from a group. The returned
 * group keeps the same `combinator` but contains only:
 *   - leaf rules where isCompleteRule(rule) === true
 *   - nested groups that themselves have at least one surviving rule
 *
 * Returns `null` when nothing survives — callers (e.g. the modal's
 * Apply handler) should clear `advancedFilters` in that case so the
 * grid drops the WHERE clause entirely.
 *
 * @param {GridAdvancedFilterGroup|null|undefined} group
 * @returns {GridAdvancedFilterGroup|null}
 */
export function removeEmptyGroups(group) {
  if (!group || !Array.isArray(group.rules)) return null;
  const out = [];
  for (const node of group.rules) {
    if (isGroupNode(node)) {
      const cleaned = removeEmptyGroups(node);
      if (cleaned) out.push(cleaned);
    } else if (isCompleteRule(node)) {
      out.push({ field: node.field, op: node.op || 'equals', value: node.value });
    }
  }
  if (out.length === 0) return null;
  return { combinator: group.combinator === 'or' ? 'or' : 'and', rules: out };
}

/**
 * Normalise an advanced-filter group for the wire / persistence. Strips
 * UI-only fields (`id`), enforces the canonical shape, and prunes empty
 * groups. Returns `null` when no rules survive.
 *
 * @param {GridAdvancedFilterGroup|null|undefined} group
 * @returns {GridAdvancedFilterGroup|null}
 */
export function normalizeAdvancedFilterGroup(group) {
  return removeEmptyGroups(group);
}

// ─────────────────────────────────────────────────────────────────
// Server-mode grouped response helpers (Stage 6A Increment 3)
//
// When a backend list endpoint returns `meta.groups` (see developer
// guide), the grid prefers those server-side aggregates over its own
// client-side computation. These helpers are pure — they just slot
// the server-supplied summary into the same pseudo-row shape that
// `groupRowsByField` emits in client mode, so the existing group-
// header renderer in ErpDataGrid renders both code paths identically.
// ─────────────────────────────────────────────────────────────────

/**
 * Look up a single server-supplied group entry by its key. The
 * comparison is loose (`==`) because the backend may stringify
 * integer keys (e.g. state codes) while the row carries them as
 * numbers. Returns `null` when no match is found.
 *
 * @param {Array<{key:any,label?:any,count?:number,summary?:Object}>|null|undefined} metaGroups
 * @param {any} groupValue
 */
export function getGroupSummaryForValue(metaGroups, groupValue) {
  if (!Array.isArray(metaGroups) || metaGroups.length === 0) return null;
  for (const g of metaGroups) {
    if (!g) continue;
    // eslint-disable-next-line eqeqeq
    if (g.key == groupValue) return g;
  }
  return null;
}

/**
 * Build the same pseudo-row sequence that `groupRowsByField` produces
 * for client mode, but use the SERVER-SUPPLIED group entries (label,
 * count, summary) instead of computing them from the current page.
 *
 * Rows that don't match any declared group key are appended at the
 * end under a synthetic "Other" header so they remain visible. This
 * fallback is defensive — a well-behaved backend should return all
 * groups for the current page.
 *
 * @param {any[]} rows
 * @param {{groups?:Array<{key:any,label?:any,count?:number,summary?:Object}>}|null|undefined} meta
 * @param {string|string[]} groupBy
 * @returns {{ rows: any[], groups: Array<{key:string,label:any,count:number,summary?:Object,rows:any[]}> }}
 */
export function buildGroupedRowsFromServerMeta(rows, meta, groupBy) {
  const metaGroups =
    meta && Array.isArray(meta.groups) ? meta.groups : null;
  // Empty / missing meta.groups → treat as "no grouping" so the
  // grid renders flat rows. Same fallback applies when groupBy is
  // not provided. The caller (ErpDataGrid) also gates on
  // `meta.groups.length > 0` before swapping to grouped rendering;
  // this branch keeps the helper safe to call directly.
  if (!metaGroups || metaGroups.length === 0 || !groupBy) {
    return { rows: rows || [], groups: [] };
  }
  // Bucket the current-page rows by their value at `groupBy` so the
  // pseudo-row headers can fan out the matching rows beneath them.
  const buckets = new Map();
  for (const r of rows || []) {
    const raw = readPath(r, groupBy);
    const k = raw === null || raw === undefined || raw === '' ? '__none__' : String(raw);
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k).push(r);
  }
  const out = [];
  const emittedKeys = new Set();
  const renderGroups = [];
  for (const g of metaGroups) {
    if (!g) continue;
    const rawKey = g.key;
    const k = rawKey === null || rawKey === undefined || rawKey === '' ? '__none__' : String(rawKey);
    emittedKeys.add(k);
    const matched = buckets.get(k) || [];
    const label = g.label === undefined || g.label === null ? rawKey : g.label;
    const count = Number.isFinite(g.count) ? g.count : matched.length;
    out.push({
      __erpGroupHeader: true,
      __erpGroupKey: k,
      __erpGroupLabel: label,
      __erpGroupCount: count,
      __erpGroupRows: matched,
      __erpGroupServerSummary: g.summary && typeof g.summary === 'object' ? g.summary : null
    });
    out.push(...matched);
    renderGroups.push({
      key: k,
      label,
      count,
      rows: matched,
      summary: g.summary || null
    });
  }
  // Surface rows whose group value the server did not declare. Avoids
  // silently dropping them — they appear under an "(Other)" header.
  const leftover = [];
  for (const [k, rs] of buckets.entries()) {
    if (!emittedKeys.has(k)) leftover.push({ key: k, rows: rs });
  }
  if (leftover.length > 0) {
    let total = 0;
    const all = [];
    for (const lo of leftover) {
      total += lo.rows.length;
      all.push(...lo.rows);
    }
    out.push({
      __erpGroupHeader: true,
      __erpGroupKey: '__other__',
      __erpGroupLabel: '(Other)',
      __erpGroupCount: total,
      __erpGroupRows: all,
      __erpGroupServerSummary: null
    });
    out.push(...all);
    renderGroups.push({ key: '__other__', label: '(Other)', count: total, rows: all, summary: null });
  }
  return { rows: out, groups: renderGroups };
}

/**
 * Reorder a column tree according to `desiredKeyOrder`. Works on both
 * flat and grouped columns:
 *   - Group cells keep their colspan structure.
 *   - Each sibling array (top-level OR a group's `children`) is sorted
 *     by its members' position in `desiredKeyOrder`.
 *   - Keys NOT in `desiredKeyOrder` retain their original relative
 *     order at the end of their sibling array.
 *
 * Pure function — never mutates inputs.
 *
 * @param {GridColumn[]} columns
 * @param {string[]} desiredKeyOrder
 */
export function applyColumnOrder(columns, desiredKeyOrder) {
  if (!Array.isArray(desiredKeyOrder) || desiredKeyOrder.length === 0) {
    return columns;
  }
  const orderIndex = new Map();
  desiredKeyOrder.forEach((k, i) => orderIndex.set(k, i));

  const sortGroup = (cols) => {
    if (!Array.isArray(cols) || cols.length === 0) return cols;
    const annotated = cols.map((c, originalIdx) => {
      const key = c?.key || c?.dataIndex;
      const order = orderIndex.has(key)
        ? orderIndex.get(key)
        : Number.MAX_SAFE_INTEGER;
      return { c, originalIdx, order };
    });
    annotated.sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.originalIdx - b.originalIdx; // stable
    });
    return annotated.map((a) => {
      const c = a.c;
      if (c && Array.isArray(c.children) && c.children.length) {
        return { ...c, children: sortGroup(c.children) };
      }
      return c;
    });
  };

  return sortGroup(columns);
}

/**
 * Walk a (possibly nested) column tree and find the column whose key
 * matches. Returns `{ siblings, index }` so callers can perform
 * adjacent-sibling operations like Move Up / Move Down without
 * re-flattening. Returns null when not found.
 *
 * @param {GridColumn[]} columns
 * @param {string} key
 */
export function findColumnAndSiblings(columns, key) {
  if (!Array.isArray(columns)) return null;
  for (let i = 0; i < columns.length; i += 1) {
    const c = columns[i];
    if ((c?.key || c?.dataIndex) === key) {
      return { siblings: columns, index: i };
    }
    if (Array.isArray(c?.children) && c.children.length) {
      const r = findColumnAndSiblings(c.children, key);
      if (r) return r;
    }
  }
  return null;
}

/**
 * Flatten a (possibly nested) column tree into the leaf-key sequence
 * that would render. Used by the reorder logic to compute a stable
 * global key list that applyColumnOrder can re-sort.
 *
 * @param {GridColumn[]} columns
 */
export function flattenColumnKeys(columns) {
  const out = [];
  const walk = (cols) => {
    for (const c of cols || []) {
      if (Array.isArray(c?.children) && c.children.length) {
        walk(c.children);
        continue;
      }
      const k = c?.key || c?.dataIndex;
      if (k) out.push(k);
    }
  };
  walk(columns);
  return out;
}

// ─────────────────────────────────────────────────────────────────
// Grouping helpers (Stage 6A Increment 2)
//
// Pure transforms used by ErpDataGrid in client mode to render
// grouped sections. Server mode does not call these — it forwards
// `groupBy` via the query and trusts the backend to return either
// already-grouped rows or grouped metadata.
//
// Sentinel keys on the pseudo header rows:
//   __erpGroupHeader: true
//   __erpGroupKey:    stable string key (used in React keys)
//   __erpGroupLabel:  human-readable label (often === key)
//   __erpGroupCount:  number of rows inside the group
//   __erpGroupRows:   the underlying row array (for aggregation)
//
// These are non-enumerable in spirit but plain properties in practice —
// the grid's render guards check `record.__erpGroupHeader` before
// emitting normal cells.
// ─────────────────────────────────────────────────────────────────

/**
 * Group an array of rows by a column dataIndex/key and insert
 * synthetic header rows between groups. The order within a group is
 * preserved from the input order, and the order BETWEEN groups
 * follows first-appearance of each group key (stable).
 *
 * @param {any[]} rows
 * @param {string|string[]} groupBy — dataIndex of the grouping column
 * @returns {{ rows: any[], groups: Array<{key:string,label:any,count:number,rows:any[]}> }}
 */
export function groupRowsByField(rows, groupBy) {
  if (!Array.isArray(rows) || rows.length === 0 || !groupBy) {
    return { rows: rows || [], groups: [] };
  }
  const buckets = new Map();
  for (const r of rows) {
    const raw = readPath(r, groupBy);
    const key = raw === null || raw === undefined || raw === '' ? '__none__' : String(raw);
    const label = key === '__none__' ? '(none)' : raw;
    if (!buckets.has(key)) {
      buckets.set(key, { key, label, count: 0, rows: [] });
    }
    const bucket = buckets.get(key);
    bucket.rows.push(r);
    bucket.count += 1;
  }
  const out = [];
  const groups = [];
  for (const bucket of buckets.values()) {
    out.push({
      __erpGroupHeader: true,
      __erpGroupKey: bucket.key,
      __erpGroupLabel: bucket.label,
      __erpGroupCount: bucket.count,
      __erpGroupRows: bucket.rows
    });
    out.push(...bucket.rows);
    groups.push({
      key: bucket.key,
      label: bucket.label,
      count: bucket.count,
      rows: bucket.rows
    });
  }
  return { rows: out, groups };
}

/**
 * Compute an aggregate value over a set of rows for a single column.
 * Returns null when the kind doesn't apply (e.g. `sum` over no numeric
 * cells). Used by the grouped-header renderer in client mode and by
 * ErpGridSummaryFooter — keeping the math in one place.
 *
 * @param {'sum'|'count'|'avg'|'min'|'max'} kind
 * @param {any[]} rows
 * @param {string|string[]} dataIndex
 */
export function aggregateRowsForColumn(kind, rows, dataIndex) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  if (kind === 'count') return rows.length;
  const nums = rows
    .map((r) => readPath(r, dataIndex))
    .filter((v) => typeof v === 'number' && Number.isFinite(v));
  switch (kind) {
    case 'sum': return nums.reduce((a, b) => a + b, 0);
    case 'avg':
      return nums.length === 0 ? null : nums.reduce((a, b) => a + b, 0) / nums.length;
    case 'min': return nums.length === 0 ? null : Math.min(...nums);
    case 'max': return nums.length === 0 ? null : Math.max(...nums);
    default:    return null;
  }
}

/**
 * Convenience: identify a group-header pseudo-row produced by
 * `groupRowsByField`. Kept central so the grid's render guards and
 * any caller-written `rowClassName` stay in sync.
 *
 * @param {any} record
 */
export function isGroupHeaderRow(record) {
  return Boolean(record && record.__erpGroupHeader === true);
}

/**
 * Apply a `GridSortSpec[]` against a row array. Stable; uses a
 * decorate-sort-undecorate pass. Returns a NEW array.
 *
 * @param {any[]} rows
 * @param {GridSortSpec[]} sort
 */
export function applyClientSort(rows, sort) {
  if (!Array.isArray(rows) || rows.length === 0) return rows || [];
  if (!Array.isArray(sort) || sort.length === 0) return rows;
  const decorated = rows.map((r, idx) => ({ r, idx }));
  decorated.sort((a, b) => {
    for (const s of sort) {
      const dir = s.direction === 'desc' ? -1 : 1;
      const va = readPath(a.r, s.field);
      const vb = readPath(b.r, s.field);
      if (va === vb) continue;
      if (va == null) return 1; // nulls last
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') {
        return (va - vb) * dir;
      }
      return String(va).localeCompare(String(vb)) * dir;
    }
    return a.idx - b.idx; // stable
  });
  return decorated.map((d) => d.r);
}

/**
 * Bridge between AntD `<Table onChange>` callback args and the
 * grid's `GridQueryState`. AntD passes the new pagination, filters,
 * and sorter; this helper produces a `GridQueryState` patch that the
 * server-mode adapter can dispatch as one unit.
 *
 * AntD sorter can be a single object or an array (when multiSort is
 * configured). Both shapes are handled.
 *
 * @param {{current?:number,pageSize?:number}} pagination
 * @param {Record<string,any[]>} filters
 * @param {Object|Object[]} sorter
 * @returns {Partial<GridQueryState>}
 */
export function fromAntdTableChange(pagination, filters, sorter) {
  const patch = {};
  if (pagination) {
    if (pagination.current) patch.page = pagination.current;
    if (pagination.pageSize) patch.pageSize = pagination.pageSize;
  }
  if (filters && typeof filters === 'object') {
    const flat = [];
    for (const [field, value] of Object.entries(filters)) {
      if (value === null || value === undefined) continue;
      if (Array.isArray(value) && value.length === 0) continue;
      flat.push({
        field,
        value: Array.isArray(value) && value.length === 1 ? value[0] : value,
        op: Array.isArray(value) && value.length > 1 ? 'in' : 'equals'
      });
    }
    patch.filters = flat;
  }
  if (sorter) {
    const arr = Array.isArray(sorter) ? sorter : [sorter];
    const sort = arr
      .filter((s) => s && s.field && s.order)
      .map((s) => ({
        field: Array.isArray(s.field) ? s.field.join('.') : String(s.field),
        direction: s.order === 'descend' ? 'desc' : 'asc'
      }));
    patch.sort = sort;
  }
  return patch;
}
